import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useSortable } from '@dnd-kit/sortable';
import { useDndContext } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  RiDraggable, RiDeleteBinLine, RiSettings3Line, RiCheckLine,
  RiEyeLine, RiEyeOffLine,
} from 'react-icons/ri';
import { FIELD_TYPES } from './constants';
import { getFieldTypeInfo, fieldTypeLabel } from './utils';
import { getLocalized } from '../../i18n/getLocalized';
import MultiLangInput from '../../components/MultiLangInput/MultiLangInput';
import OptionsEditor, { RadioOptionsEditor } from './OptionsEditor';

// Width map: CSS grid column span (out of 12)
const WIDTH_OPTIONS = [
  { key: 'full',    labelKey: 'fieldsManager.fieldRow.widthFull',    span: 12, short: '1/1' },
  { key: 'half',    labelKey: 'fieldsManager.fieldRow.widthHalf',    span: 6,  short: '1/2' },
  { key: 'third',   labelKey: 'fieldsManager.fieldRow.widthThird',   span: 4,  short: '1/3' },
  { key: 'quarter', labelKey: 'fieldsManager.fieldRow.widthQuarter', span: 3,  short: '1/4' },
];

// ─── WYSIWYG preview input per field type ──────────────────────────────────
function FieldPreview({ field, typeInfo, t, lang }) {
  const label = getLocalized(field.label, lang);
  const ph = getLocalized(field.placeholder, lang)
    || t('fieldsManager.fieldRow.enterValue', {
      label: (label || fieldTypeLabel(t, typeInfo)).toLowerCase(),
    });

  switch (field.type) {
    case 'textarea':
      return <textarea className="fm-wys-input fm-wys-input--textarea" placeholder={ph} rows={2} disabled />;
    case 'number':
      return <input type="number" className="fm-wys-input" placeholder={getLocalized(field.placeholder, lang) || '0'} disabled />;
    case 'date':
      return <input type="date" className="fm-wys-input" disabled />;
    case 'email':
      return <input type="email" className="fm-wys-input" placeholder={getLocalized(field.placeholder, lang) || 'name@example.com'} disabled />;
    case 'phone':
      return <input type="tel" className="fm-wys-input" placeholder={getLocalized(field.placeholder, lang) || '+1 (555) 000-0000'} disabled />;
    case 'currency':
      return (
        <div className="fm-wys-currency">
          <span>$</span>
          <input type="number" className="fm-wys-input" placeholder="0.00" disabled />
        </div>
      );
    case 'file':
      return (
        <div className="fm-wys-file">
          <span className="fm-wys-file__btn">{t('fieldsManager.fieldRow.chooseFile')}</span>
          <span className="fm-wys-file__hint">{t('fieldsManager.fieldRow.noFileChosen')}</span>
        </div>
      );
    case 'checkbox':
      return (
        <label className="fm-wys-checkbox">
          <input type="checkbox" disabled />
          <span>{label || t('fieldsManager.fieldRow.checkboxOption')}</span>
        </label>
      );
    case 'select':
      return (
        <select className="fm-wys-input fm-wys-select" disabled defaultValue="">
          <option value="" disabled>{t('fieldsManager.fieldRow.selectAnOption')}</option>
          {field.options.map((o) => <option key={o.id}>{getLocalized(o.label, lang) || '—'}</option>)}
        </select>
      );
    case 'radio':
      // Radio is handled inline via RadioOptionsEditor (it IS the wysiwyg view)
      return null;
    default:
      return <input type="text" className="fm-wys-input" placeholder={ph} disabled />;
  }
}

// ─── Portal-positioned floating menu hook ─────────────────────────────────
function useFloatingMenu(open, anchorRef, { align = 'right', gap = 6 } = {}) {
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef.current) { setCoords(null); return; }
    const compute = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + gap,
        left: align === 'right' ? null : rect.left,
        right: align === 'right' ? (window.innerWidth - rect.right) : null,
      });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [open, anchorRef, gap, align]);

  return coords;
}

// ─── Type switcher popover ─────────────────────────────────────────────────
function TypeSwitcher({ currentType, onChange, color, t }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const current = getFieldTypeInfo(currentType);
  const coords = useFloatingMenu(open, btnRef);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const Icon = current.icon;
  return (
    <div className="fm-type-switch">
      <button
        ref={btnRef}
        type="button"
        className="fm-type-switch__btn"
        style={{ '--ft-color': color, '--ft-bg': current.bg }}
        onClick={() => setOpen((v) => !v)}
        title={t('fieldsManager.fieldRow.changeType', { label: fieldTypeLabel(t, current) })}
      >
        <Icon />
        <span>{fieldTypeLabel(t, current)}</span>
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          className="fm-type-switch__menu fm-floating-menu"
          style={{ position: 'fixed', top: coords.top, right: coords.right, left: coords.left }}
        >
          {FIELD_TYPES.map((ft) => {
            const I = ft.icon;
            const active = ft.type === currentType;
            return (
              <button
                key={ft.type}
                type="button"
                className={`fm-type-switch__item ${active ? 'is-active' : ''}`}
                style={{ '--ft-color': ft.color, '--ft-bg': ft.bg }}
                onClick={() => { onChange(ft.type); setOpen(false); }}
              >
                <span className="fm-type-switch__item-icon"><I /></span>
                <span>{fieldTypeLabel(t, ft)}</span>
                {active && <RiCheckLine className="fm-type-switch__check" />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─── Width chip ────────────────────────────────────────────────────────────
function WidthChip({ width, onChange, t }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const current = WIDTH_OPTIONS.find((w) => w.key === width) || WIDTH_OPTIONS[0];
  const coords = useFloatingMenu(open, btnRef);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div className="fm-width-chip">
      <button
        ref={btnRef}
        type="button"
        className="fm-width-chip__btn"
        onClick={() => setOpen((v) => !v)}
        title={t('fieldsManager.fieldRow.widthTitle', { label: t(current.labelKey) })}
      >
        <span className="fm-width-chip__glyph" data-width={current.key}>
          <i /><i /><i /><i />
        </span>
        <span className="fm-width-chip__label">{current.short}</span>
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          className="fm-width-chip__menu fm-floating-menu"
          style={{ position: 'fixed', top: coords.top, right: coords.right, left: coords.left }}
        >
          {WIDTH_OPTIONS.map((w) => (
            <button
              key={w.key}
              type="button"
              className={`fm-width-chip__opt ${w.key === width ? 'is-active' : ''}`}
              onClick={() => { onChange(w.key); setOpen(false); }}
            >
              <span className="fm-width-chip__glyph" data-width={w.key}>
                <i /><i /><i /><i />
              </span>
              <span>{t(w.labelKey)}</span>
              <span className="fm-width-chip__short">{w.short}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─── Right-edge resize handle (drag to change width) ──────────────────────
function WidthResizeHandle({ currentWidth, onChange, t }) {
  const [dragging, setDragging] = useState(false);
  const startRef = useRef(null);

  const onPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fieldEl = e.currentTarget.closest('.fm-field');
    const gridEl = fieldEl?.closest('.fm-field-grid');
    if (!fieldEl || !gridEl) return;

    const gridRect = gridEl.getBoundingClientRect();
    const colWidth = gridRect.width / 12; // 12-col grid
    const fieldRect = fieldEl.getBoundingClientRect();
    const fieldLeft = fieldRect.left - gridRect.left;

    startRef.current = { startX: e.clientX, fieldLeft, colWidth };
    setDragging(true);

    const keyBySpan = { 12: 'full', 6: 'half', 4: 'third', 3: 'quarter' };
    const validSpans = [3, 4, 6, 12];

    const onMove = (ev) => {
      const { startX, fieldLeft: fl, colWidth: cw } = startRef.current;
      const desiredRight = (ev.clientX - gridRect.left);
      const desiredWidth = desiredRight - fl;
      const desiredSpan = Math.max(1, Math.round(desiredWidth / cw));
      // snap to nearest valid span
      const snapped = validSpans.reduce((best, s) =>
        Math.abs(s - desiredSpan) < Math.abs(best - desiredSpan) ? s : best, 12);
      const key = keyBySpan[snapped];
      if (key && key !== currentWidth) onChange(key);
      // suppress unused
      void startX; void fl; void cw;
    };
    const onUp = () => {
      setDragging(false);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  return (
    <span
      className={`fm-field__resize ${dragging ? 'is-dragging' : ''}`}
      onPointerDown={onPointerDown}
      title={t('fieldsManager.fieldRow.resizeWidth')}
    >
      <i />
    </span>
  );
}

// ─── Main FieldRow ─────────────────────────────────────────────────────────
export default function FieldRow({ field, sectionId, sectionFields, onUpdate, onRemove, errors, isDragOverlay }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [showMore, setShowMore] = useState(false);
  const typeInfo = getFieldTypeInfo(field.type);
  const width = field.width || 'full';
  const widthInfo = WIDTH_OPTIONS.find((w) => w.key === width) || WIDTH_OPTIONS[0];

  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id: field.id,
    data: { type: 'field', sectionId, fieldId: field.id },
    disabled: !!isDragOverlay,
  });

  // While a library tile hovers this row, show where the new field will land:
  // it is inserted *before* this one, so draw a caret on its leading edge.
  const { active } = useDndContext();
  const insertBefore = !isDragOverlay
    && isOver
    && typeof active?.id === 'string'
    && active.id.startsWith('sidebar-field-');

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    '--field-color': typeInfo.color,
    '--field-bg': typeInfo.bg,
    gridColumn: `span ${widthInfo.span}`,
  };

  const update = (key, val) => onUpdate(sectionId, field.id, { [key]: val });

  // Radio sibling fields (excluding this one) for visibility targeting
  const siblingFields = (sectionFields || []).filter((f) => f.id !== field.id);

  const overlayStyle = {
    '--field-color': typeInfo.color,
    '--field-bg': typeInfo.bg,
    gridColumn: `span ${widthInfo.span}`,
  };

  return (
    <div
      ref={setNodeRef}
      style={isDragOverlay ? overlayStyle : style}
      className={`fm-field ${isDragging ? 'fm-field--dragging' : ''} ${isDragOverlay ? 'fm-field--overlay' : ''} ${insertBefore ? 'fm-field--insert-before' : ''} ${field.isActive === false ? 'fm-field--passive' : ''}`}
    >
      {/* ── Header: drag + label + required star + controls (hover reveal) ── */}
      <div className="fm-field__head">
        <span
          className="fm-drag-handle fm-drag-handle--field"
          {...(isDragOverlay ? {} : { ...attributes, ...listeners })}
          title={t('fieldsManager.fieldRow.drag')}
        >
          <RiDraggable />
        </span>

        <div className="fm-field__label-wrap">
          <MultiLangInput
            value={field.label}
            onChange={(v) => update('label', v)}
            primaryLang={lang}
            placeholder={t('fieldsManager.fieldRow.untitledField')}
            className="fm-ml fm-ml--label"
            inputClassName={`fm-field__label ${errors[`field-${field.id}-label`] ? 'fm-input--error' : ''}`}
          />
          {field.required && <span className="fm-req-star" title={t('fieldsManager.fieldRow.required')}>*</span>}
          {errors[`field-${field.id}-label`] && (
            <span className="fm-error-text">{errors[`field-${field.id}-label`]}</span>
          )}
        </div>

        <div className="fm-field__controls">
          <WidthChip width={width} onChange={(w) => update('width', w)} t={t} />
          <TypeSwitcher
            currentType={field.type}
            onChange={(next) => update('type', next)}
            color={typeInfo.color}
            t={t}
          />
          <button
            type="button"
            className={`fm-ctrl-btn ${field.required ? 'is-on' : ''}`}
            onClick={() => update('required', !field.required)}
            title={field.required ? t('fieldsManager.fieldRow.makeOptional') : t('fieldsManager.fieldRow.makeRequired')}
          >
            <span className="fm-ctrl-btn__dot" />
            <span>{t('fieldsManager.fieldRow.required')}</span>
          </button>
          <button
            type="button"
            className={`fm-ctrl-icon ${showMore ? 'is-on' : ''}`}
            onClick={() => setShowMore((v) => !v)}
            title={t('fieldsManager.fieldRow.moreOptions')}
          >
            <RiSettings3Line />
          </button>
          {field.isStandard ? (
            // Standard fields: no delete — only active/passive.
            <button
              type="button"
              className={`fm-ctrl-icon ${field.isActive === false ? 'is-on' : ''}`}
              onClick={() => update('isActive', field.isActive === false)}
              title={field.isActive === false
                ? t('fieldsManager.standardField.activate', { defaultValue: 'Activate this field' })
                : t('fieldsManager.standardField.note', { defaultValue: 'This standard field cannot be deleted. It can be deactivated if needed.' })}
            >
              {field.isActive === false ? <RiEyeOffLine /> : <RiEyeLine />}
            </button>
          ) : (
            <button
              type="button"
              className="fm-ctrl-icon fm-ctrl-icon--danger"
              onClick={() => onRemove(sectionId, field.id)}
              title={t('fieldsManager.fieldRow.deleteField')}
            >
              <RiDeleteBinLine />
            </button>
          )}
        </div>
      </div>

      {/* ── WYSIWYG body: actual rendered input ── */}
      {field.type !== 'radio' && field.type !== 'select' && field.type !== 'checkbox' && (
        <div className="fm-field__body">
          <FieldPreview field={field} typeInfo={typeInfo} t={t} lang={lang} />
        </div>
      )}

      {field.type === 'checkbox' && (
        <div className="fm-field__body fm-field__body--inline">
          <FieldPreview field={field} typeInfo={typeInfo} t={t} lang={lang} />
        </div>
      )}

      {/* ── Select: show live dropdown preview + options below ── */}
      {field.type === 'select' && (
        <>
          <div className="fm-field__body">
            <FieldPreview field={field} typeInfo={typeInfo} t={t} lang={lang} />
          </div>
          <OptionsEditor
            options={field.options}
            onChange={(opts) => update('options', opts)}
            error={errors[`field-${field.id}-options`]}
          />
        </>
      )}

      {/* ── Radio: compact chip editor IS the WYSIWYG view ── */}
      {field.type === 'radio' && (
        <RadioOptionsEditor
          options={field.options}
          onChange={(opts) => update('options', opts)}
          error={errors[`field-${field.id}-options`]}
          allFields={siblingFields}
          defaultRule={field.defaultVisibilityRule || null}
          onDefaultRuleChange={(rule) => update('defaultVisibilityRule', rule)}
        />
      )}

      {/* ── Right-edge resize handle (not shown in drag overlay) ── */}
      {!isDragOverlay && (
        <WidthResizeHandle currentWidth={width} onChange={(w) => update('width', w)} t={t} />
      )}

      {/* ── Expanded: placeholder customization ── */}
      {showMore && (
        <div className="fm-field__more">
          <div className="fm-field__more-row">
            <span>{t('fieldsManager.fieldRow.placeholder')}</span>
            <MultiLangInput
              value={field.placeholder}
              onChange={(v) => update('placeholder', v)}
              primaryLang={lang}
              placeholder={t('fieldsManager.fieldRow.placeholderHint', {
                type: fieldTypeLabel(t, typeInfo).toLowerCase(),
              })}
              className="fm-ml fm-ml--more"
              inputClassName="fm-input fm-input--sm"
            />
          </div>
          <div className="fm-field__more-row">
            <span>{t('fieldsManager.fieldRow.description')}</span>
            <MultiLangInput
              value={field.description}
              onChange={(v) => update('description', v)}
              primaryLang={lang}
              placeholder={t('fieldsManager.fieldRow.descriptionHint')}
              multiline
              rows={2}
              className="fm-ml fm-ml--more"
              inputClassName="fm-input fm-input--sm"
            />
          </div>
        </div>
      )}
    </div>
  );
}
